import React from 'react';
import { AlertTriangle, Stethoscope, BookOpen, UserCheck } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import FloatingButtons from '../components/common/FloatingButtons';

const MedicalDisclaimerPage = () => {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative h-[40vh] flex items-center justify-center"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/685a4a49-d889-47d7-87c9-a5d959d86d4d/images/e063e0c345b4c6425a98687d6b79f4e182c52f5a791cd47fff2baebdfc80769c.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center text-white px-6">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-amber-400" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">Medical Disclaimer</h1>
          <p className="text-lg text-gray-200">Important Information About Our Services</p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
          
          {/* Critical Warning Box */}
          <div className="bg-red-50 border-2 border-red-200 p-6 mb-8 rounded-xl">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-10 h-10 text-red-500 flex-shrink-0" />
              <div>
                <h3 className="text-red-800 font-bold text-xl m-0 mb-3">IMPORTANT MEDICAL DISCLAIMER</h3>
                <p className="text-red-700 m-0 text-lg">
                  The content provided on Doktor Gigi is for <strong>educational and informational purposes only</strong>. 
                  It is <strong>NOT a substitute</strong> for professional medical advice, diagnosis, or treatment.
                </p>
              </div>
            </div>
          </div>

          {/* Key Points */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gray-50 p-6 rounded-xl">
              <BookOpen className="w-10 h-10 text-teal-500 mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Educational Purpose Only</h3>
              <p className="text-gray-600 text-sm">
                All articles, information, and content on this website are intended solely for educational purposes to help you understand kidney health better.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <Stethoscope className="w-10 h-10 text-teal-500 mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">No Medical Treatment</h3>
              <p className="text-gray-600 text-sm">
                We do not provide medical diagnosis, treatment, or prescriptions. Our platform connects you with specialists who provide these services independently.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <AlertTriangle className="w-10 h-10 text-amber-500 mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Not a Substitute</h3>
              <p className="text-gray-600 text-sm">
                Information on this website should never be used as a substitute for direct medical advice from qualified healthcare professionals.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <UserCheck className="w-10 h-10 text-teal-500 mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Consult a Doctor</h3>
              <p className="text-gray-600 text-sm">
                Always seek the advice of a qualified, registered medical practitioner for any questions regarding your health condition.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. No Diagnosis or Prescription</h2>
          <p className="text-gray-600 mb-6">
            Doktor Gigi and its content <strong>do not provide</strong>:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Medical diagnosis of any condition</li>
            <li>Prescriptions for medications</li>
            <li>Specific treatment recommendations</li>
            <li>Second opinions on existing medical advice</li>
          </ul>
          <p className="text-gray-600 mb-6">
            Any symptom checkers, questionnaires, or informational tools on our platform are for educational purposes only and should not be relied upon for medical decisions.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Always Consult a Registered Doctor</h2>
          <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-6 rounded-r-lg">
            <p className="text-gray-700 m-0">
              <strong>If you have any concerns about your health:</strong> Please consult a registered medical practitioner immediately. 
              Do not delay seeking medical attention based on information you have read on this website.
            </p>
          </div>
          <p className="text-gray-600 mb-6">
            You should always:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Consult with a qualified healthcare provider before making any health-related decisions</li>
            <li>Inform your doctor about all symptoms, medications, and medical history</li>
            <li>Follow your doctor's advice regarding treatment and follow-up care</li>
            <li>Seek emergency medical care for any urgent health concerns</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Information Accuracy</h2>
          <p className="text-gray-600 mb-6">
            While we strive to provide accurate and up-to-date information:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Medical knowledge evolves constantly, and information may become outdated</li>
            <li>Individual health conditions vary, and general information may not apply to your specific situation</li>
            <li>We cannot guarantee the completeness or accuracy of all information presented</li>
            <li>We recommend verifying information with your healthcare provider</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Emergency Situations</h2>
          <div className="bg-red-50 border-2 border-red-300 p-6 mb-6 rounded-xl">
            <h3 className="text-red-800 font-bold m-0 mb-3">⚠️ Medical Emergency?</h3>
            <p className="text-red-700 m-0 mb-4">
              If you believe you are experiencing a medical emergency, <strong>DO NOT</strong> use this website. Instead:
            </p>
            <ul className="list-disc pl-6 text-red-700 space-y-2 m-0">
              <li>Call emergency services immediately (Dial 112 in India)</li>
              <li>Go to the nearest hospital emergency room</li>
              <li>Contact your local emergency medical services</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Third-Party Content</h2>
          <p className="text-gray-600 mb-6">
            Our website may contain links to third-party websites or content. We are not responsible for the accuracy, completeness, or reliability of any information provided by third parties. Use of such content is at your own risk.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. No Doctor-Patient Relationship</h2>
          <p className="text-gray-600 mb-6">
            Using this website or reading its content does not establish a doctor-patient relationship between you and Doktor Gigi. A doctor-patient relationship is only formed when you directly consult with a registered medical practitioner through our referral services or otherwise.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Acknowledgment</h2>
          <div className="bg-gray-100 p-6 rounded-xl">
            <p className="text-gray-700 m-0">
              By using this website, you acknowledge that you have read, understood, and agree to this Medical Disclaimer. 
              You accept full responsibility for any decisions you make based on the information provided on this platform.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Contact Us</h2>
          <p className="text-gray-600 mb-4">
            If you have questions about this Medical Disclaimer, please contact us at:
          </p>
          <p className="text-gray-600">
            <strong>Email:</strong> doktorgigi4@gmail.com
          </p>
        </div>
      </section>
    </div>
    <Footer />
    <FloatingButtons />
    </>
  );
};

export default MedicalDisclaimerPage;
